import { get, set } from 'idb-keyval';
import type { Book, ProgressFile } from '../types';
import { EMPTY_PROGRESS } from '../types';

/** IndexedDB-backed cache. Two jobs:
 *   1. progress survives a reload instantly, before any network round trip;
 *   2. the scanned library renders immediately on a cold start.
 */

const progressKey = (sourceKey: string) => `shelf:progress:${sourceKey}`;
const libraryKey = (sourceKey: string) => `shelf:library:${sourceKey}`;

export async function readLocalProgress(sourceKey: string): Promise<ProgressFile> {
  try {
    const stored = await get<ProgressFile>(progressKey(sourceKey));
    return stored?.books ? stored : EMPTY_PROGRESS;
  } catch {
    return EMPTY_PROGRESS;
  }
}

export async function writeLocalProgress(
  sourceKey: string,
  progress: ProgressFile,
): Promise<void> {
  try {
    await set(progressKey(sourceKey), progress);
  } catch {
    /* Private-mode browsers can refuse IndexedDB; playback must not break. */
  }
}

export interface CachedLibrary {
  books: Book[];
  cachedAt: string;
}

export async function readCachedLibrary(sourceKey: string): Promise<CachedLibrary | null> {
  try {
    return (await get<CachedLibrary>(libraryKey(sourceKey))) ?? null;
  } catch {
    return null;
  }
}

export async function writeCachedLibrary(sourceKey: string, books: Book[]): Promise<void> {
  try {
    await set(libraryKey(sourceKey), { books, cachedAt: new Date().toISOString() });
  } catch {
    /* non-fatal */
  }
}

/** A stable, human-readable name for this device, used in progress records so
 *  the resume prompt can say where a position came from. */
export function deviceLabel(): string {
  const KEY = 'shelf.deviceLabel';
  const existing = localStorage.getItem(KEY);
  if (existing) return existing;

  const ua = navigator.userAgent;
  const platform =
    /iPhone|iPod/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Macintosh/.test(ua) ? 'Mac'
    : /Windows/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux'
    : 'device';
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : 'browser';

  const label = `${platform} ${browser}`;
  localStorage.setItem(KEY, label);
  return label;
}
